/**
 * One socket connection per signed-in session, and the client-side mirror of what the
 * server pushes. The server is authoritative: this hook never derives game state, it only
 * stores the latest snapshot and forwards requests.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type {
  Ack,
  AckResult,
  CardDef,
  CardId,
  ChatMessage,
  Emote,
  ClientToServerEvents,
  GameRecord,
  GameView,
  IceServer,
  Invite,
  LobbySummary,
  Me,
  Notice,
  OnlinePlayer,
  PlayerId,
  ServerToClientEvents,
} from '@kadice/shared';
import { EMOTE_SHOW_MS, t } from '@kadice/shared';
import { playSound } from './audio';
import { bumpAvatar } from './avatars';
import { useVoice, type VoiceEvents } from './voice';

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
type Event = keyof ClientToServerEvents;
/** Arguments of a request, minus the trailing ack. */
type Args<E extends Event> = Parameters<ClientToServerEvents[E]> extends [...infer A, Ack<never>] ? A : never;
/** Data the server acks a request with. */
type Reply<E extends Event> = Parameters<ClientToServerEvents[E]> extends [...unknown[], Ack<infer T>] ? T : never;

export type Connection = 'connecting' | 'online' | 'offline';
export interface ToastNotice extends Notice {
  id: number;
}
/** An emote showing over its sender's seat (see `EMOTE_SHOW_MS`). */
export interface ShownEmote {
  id: number;
  playerId: PlayerId;
  name: string;
  emote: Emote;
}
/** Chat lines and game notices in arrival order (the chat tray shows both; see `Chat`). */
export type FeedItem = { id: number; chat: ChatMessage; notice?: never } | { id: number; notice: Notice; chat?: never };

const REQUEST_TIMEOUT_MS = 10_000;
const NOTICE_TTL_MS = 6_000;
const CHAT_HISTORY = 100;
const NOTICE_LOG = 30;
const FEED_MAX = 200;
// Reload the page once the socket has been down this long and the page is at least this old
// (the age floor stops a reload loop while the server is down).
const RELOAD_OFFLINE_MS = 30_000;
const RELOAD_MIN_AGE_MS = 5 * 60_000;
const RELOAD_CHECK_MS = 5_000;
const pageLoadedAt = Date.now();
let feedSeq = 0;

export function useGameClient(onUnauthorized: () => void) {
  const socketRef = useRef<GameSocket | null>(null);
  const roomRef = useRef<number | null>(null);
  const meRef = useRef<Me | null>(null);
  const viewRef = useRef<GameView | null>(null);
  const [connection, setConnection] = useState<Connection>('connecting');
  const [me, setMe] = useState<Me | null>(null);
  const [catalog, setCatalog] = useState<ReadonlyMap<CardId, CardDef>>(new Map());
  const [lobbies, setLobbies] = useState<LobbySummary[]>([]);
  /** The lobby list has arrived at least once (it only comes while you're not in a game). */
  const [listed, setListed] = useState(false);
  const [view, setView] = useState<GameView | null>(null);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [online, setOnline] = useState<OnlinePlayer[]>([]);
  const [notices, setNotices] = useState<ToastNotice[]>([]);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  /** Records you set in a game that just finished (shown on its game over screen). */
  const [records, setRecords] = useState<{ gameId: number; records: GameRecord[] } | null>(null);
  /** A rematch you were invited to (shown in the main lobby until joined or dismissed). */
  const [invite, setInvite] = useState<Invite | null>(null);
  /** Emotes showing now, at most one per player (the latest). */
  const [emotes, setEmotes] = useState<ShownEmote[]>([]);
  const [voiceMembers, setVoiceMembers] = useState<{ gameId: number; ids: PlayerId[] } | null>(null);
  const iceServers = useRef<IceServer[]>([]);
  const voiceEvents = useRef<VoiceEvents>({ signal: () => {}, end: () => {} });
  const addToFeed = useCallback(
    (item: { chat: ChatMessage } | { notice: Notice }) =>
      setFeed((f) => [...f.slice(-(FEED_MAX - 1)), { id: ++feedSeq, ...item } as FeedItem]),
    [],
  );
  // Recent notices with timestamps, kept for feedback reports (toasts expire after a few seconds).
  const noticeLog = useRef<(Notice & { at: string })[]>([]);

  const notify = useCallback(
    (notice: Notice) => {
      noticeLog.current = [...noticeLog.current.slice(-(NOTICE_LOG - 1)), { ...notice, at: new Date().toISOString() }];
      const id = Date.now() + Math.random();
      setNotices((n) => [...n, { ...notice, id }]);
      addToFeed({ notice });
      setTimeout(() => setNotices((n) => n.filter((x) => x.id !== id)), NOTICE_TTL_MS);
    },
    [addToFeed],
  );

  useEffect(() => {
    const socket: GameSocket = io({ withCredentials: true });
    socketRef.current = socket;

    // Turn alerts are pushed only to players with no tab on screen (see turnAlerts.ts).
    const reportVisible = () => {
      if (socket.connected)
        socket.emit('client:visible', { visible: document.visibilityState === 'visible' }, () => {});
    };
    document.addEventListener('visibilitychange', reportVisible);

    let offlineSince: number | null = Date.now();
    const goOffline = () => {
      setConnection('offline');
      offlineSince ??= Date.now();
    };
    const reloadCheck = setInterval(() => {
      const now = Date.now();
      if (
        !socket.connected &&
        offlineSince !== null &&
        now - offlineSince >= RELOAD_OFFLINE_MS &&
        now - pageLoadedAt >= RELOAD_MIN_AGE_MS
      )
        location.reload();
    }, RELOAD_CHECK_MS);

    socket.on('connect', () => {
      offlineSince = null;
      setConnection('online');
      reportVisible();
    });
    socket.on('disconnect', () => {
      goOffline();
      voiceEvents.current.end();
    });
    socket.on('connect_error', (err) => {
      goOffline();
      if (err.message === 'unauthorized') onUnauthorized();
    });
    socket.on('session:hello', (hello) => {
      meRef.current = hello.me;
      setMe(hello.me);
      setCatalog(new Map(hello.catalog.map((c) => [c.id, c])));
      iceServers.current = hello.iceServers;
    });
    socket.on('lobby:list', (list) => {
      setLobbies(list);
      setListed(true);
    });
    socket.on('game:state', (next) => {
      const prev = viewRef.current;
      viewRef.current = next;
      setView(next);
      // A player (not a bot) joined the game lobby you're in.
      if (prev && next && prev.gameId === next.gameId && next.phase === 'lobby') {
        const seen = new Set(prev.players.map((p) => p.id));
        if (next.players.some((p) => !p.bot && !seen.has(p.id) && p.id !== next.you.id)) playSound('buddyIn');
      }
      // Room chat belongs to a room (main lobby or one game); start fresh when the room changes.
      // Private messages follow you.
      const room = next?.gameId ?? null;
      if (room !== roomRef.current) {
        roomRef.current = room;
        setChat((c) => c.filter((m) => m.toId !== undefined));
        setFeed((f) => f.filter((x) => x.chat?.toId !== undefined));
      }
    });
    socket.on('game:notice', notify);
    socket.on('game:records', setRecords);
    socket.on('chat:message', (m) => {
      setChat((c) => [...c.slice(-(CHAT_HISTORY - 1)), m]);
      addToFeed({ chat: m });
      if (m.toId !== undefined && m.toId === meRef.current?.id) playSound('im');
    });
    socket.on('players:online', setOnline);
    socket.on('lobby:invite', setInvite);
    socket.on('emote', (e) => {
      const id = ++feedSeq;
      setEmotes((list) => [...list.filter((x) => x.playerId !== e.playerId), { id, ...e }]);
      setTimeout(() => setEmotes((list) => list.filter((x) => x.id !== id)), EMOTE_SHOW_MS);
      // Also a line in the room's chat log (client-side only; emotes aren't stored).
      const line: ChatMessage = {
        fromId: e.playerId,
        from: e.name,
        text: t(`emote.${e.emote}`),
        at: new Date().toISOString(),
      };
      setChat((c) => [...c.slice(-(CHAT_HISTORY - 1)), line]);
      addToFeed({ chat: line });
    });
    socket.on('avatar:changed', ({ playerId, v }) => bumpAvatar(playerId, v));
    socket.on('voice:members', setVoiceMembers);
    socket.on('voice:signal', ({ from, data }) => voiceEvents.current.signal(from, data));
    socket.on('voice:ended', () => voiceEvents.current.end());

    return () => {
      clearInterval(reloadCheck);
      document.removeEventListener('visibilitychange', reportVisible);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [notify, onUnauthorized, addToFeed]);

  /**
   * Send a request and resolve with the server's ack data. Rejections are shown as an
   * error notice and resolve to undefined, so callers only handle the success path.
   */
  const request = useCallback(
    async <E extends Event>(event: E, ...args: Args<E>): Promise<Reply<E> | undefined> => {
      const socket = socketRef.current;
      if (!socket?.connected) {
        notify({ kind: 'error', text: t('error.notConnected') });
        return undefined;
      }
      // socket.io's typed emitWithAck can't follow a generic event name; the contract is enforced by Args/Reply.
      const emit = socket.timeout(REQUEST_TIMEOUT_MS) as unknown as {
        emitWithAck(event: string, ...args: unknown[]): Promise<AckResult<Reply<E>>>;
      };
      try {
        const result = await emit.emitWithAck(event, ...args);
        if (result.ok) return result.data;
        notify({ kind: 'error', text: result.error });
      } catch {
        notify({ kind: 'error', text: t('error.noResponse') });
      }
      return undefined;
    },
    [notify],
  );

  const voice = useVoice({
    socket: socketRef,
    iceServers,
    me: me?.id ?? null,
    gameId: view?.gameId ?? null,
    members: voiceMembers,
    join: useCallback(() => request('voice:join'), [request]),
    notify,
  });
  useEffect(() => {
    voiceEvents.current = voice.events;
  }, [voice.events]);

  return useMemo(
    () => ({
      connection,
      me,
      setMe,
      catalog,
      lobbies,
      listed,
      view,
      chat,
      online,
      feed,
      notices,
      records,
      invite,
      dismissInvite: () => setInvite(null),
      emotes,
      noticeLog,
      notify,
      request,
      voice,
    }),
    [
      connection,
      me,
      catalog,
      lobbies,
      listed,
      view,
      chat,
      online,
      feed,
      notices,
      records,
      invite,
      emotes,
      notify,
      request,
      voice,
    ],
  );
}

export type GameClient = ReturnType<typeof useGameClient>;
